import { ItemPicker } from "@/components/ItemPicker";
import { IssueRow } from "@/components/trial/IssueRow";
import { MarkupText } from "@/components/trial/MarkupText";
import { cn } from "@/lib/utils";
import { ChevronDown, CircleAlert, Shield, Skull, Trophy } from "lucide-react";
import type { TrialStage, TrialIssue, SpawnData, MonolithValues, StageModifier } from "@/lib/trial-schema";
import {
  MONOLITH_EFFECTS,
  CHALLENGE_TYPES,
  monolithDescription,
  challengeDescription,
  usesAttackPotency,
} from "@/lib/trial-defense";
import { TRIAL_ENEMIES, TRIAL_SPAWN_POSITIONS } from "@/lib/trial-catalog";
import { REWARD_TYPE_NAMES } from "@/types/cloud-data";
import {
  addWave,
  removeWave,
  addSpawn,
  removeSpawn,
  updateSpawn,
  addChallenge,
  removeChallenge,
  updateChallenge,
  addReward,
  removeReward,
  updateReward,
  updateDefenseSystem,
  updateMonolithValues,
  updateModifier,
  ordinal,
} from "@/lib/trial-edit";

type Updater = (updater: (s: TrialStage) => TrialStage) => void;

const selectClass =
  "h-8 rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";
const numberClass =
  "h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring";

interface Props {
  stage: TrialStage;
  issues: TrialIssue[];
  onUpdate: Updater;
}

export function FloorCard({ stage, issues, onUpdate }: Props) {
  const errors = issues.filter((issue) => issue.severity === "error").length;
  const warnings = issues.filter((issue) => issue.severity === "warning").length;

  return (
    <div className="space-y-4 min-w-0">
      <h2 className="text-lg font-semibold">Floor {stage.floor}</h2>

      <FoldoutGroup
        icon={<Shield className="h-5 w-5" aria-hidden="true" />}
        title="Defense System"
        summary={<MarkupText text={monolithDescription(stage.defenseSystem, stage.monolithValues)} />}
        tone="text-sky-300 bg-sky-400/10"
      >
        <DefenseSystemSection stage={stage} onUpdate={onUpdate} />
        <ModifiersSection stage={stage} onUpdate={onUpdate} />
      </FoldoutGroup>

      <FoldoutGroup
        icon={<Skull className="h-5 w-5" aria-hidden="true" />}
        title="Enemy Waves"
        summary={`${stage.waveData.length} wave${stage.waveData.length === 1 ? "" : "s"} · ${stage.waveData.reduce((count, wave) => count + wave.spawnData.length, 0)} enemies`}
        tone="text-rose-300 bg-rose-400/10"
      >
        <WavesSection stage={stage} onUpdate={onUpdate} />
      </FoldoutGroup>

      <FoldoutGroup
        icon={<Trophy className="h-5 w-5" aria-hidden="true" />}
        title="Challenges"
        summary={`${stage.challenges.length} challenge${stage.challenges.length === 1 ? "" : "s"} · ${stage.reward.length} reward${stage.reward.length === 1 ? "" : "s"}`}
        tone="text-amber-300 bg-amber-400/10"
      >
        <ChallengesSection stage={stage} onUpdate={onUpdate} />
        <RewardsSection stage={stage} onUpdate={onUpdate} />
      </FoldoutGroup>

      {issues.length > 0 && (
        <FoldoutGroup
          icon={<CircleAlert className="h-5 w-5" aria-hidden="true" />}
          title="Errors & Warnings"
          summary={`${errors} error${errors === 1 ? "" : "s"} · ${warnings} warning${warnings === 1 ? "" : "s"}`}
          tone={errors > 0 ? "text-red-300 bg-red-400/10" : "text-amber-300 bg-amber-400/10"}
        >
          <div className="space-y-1.5">
            {issues.map((issue, i) => (
              <IssueRow key={i} issue={issue} />
            ))}
          </div>
        </FoldoutGroup>
      )}
    </div>
  );
}

function FoldoutGroup({
  icon,
  title,
  summary,
  tone,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  summary: React.ReactNode;
  tone: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group overflow-hidden rounded-lg border border-border bg-card">
      <summary className="flex cursor-pointer list-none items-center gap-3 px-4 py-3 marker:hidden [&::-webkit-details-marker]:hidden">
        <span className={cn("grid h-9 w-9 shrink-0 place-items-center rounded-md", tone)}>{icon}</span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-foreground">{title}</span>
          <span className="block truncate text-xs text-muted-foreground">{summary}</span>
        </span>
        <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" aria-hidden="true" />
      </summary>
      <div className="space-y-5 border-t border-border px-4 py-4">{children}</div>
    </details>
  );
}

// ─── Small field primitives ───────────────────────────────────────────────

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <input
        type="number"
        value={Number.isFinite(value) ? value : 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        className={numberClass}
      />
    </label>
  );
}

/** A <select> that never silently jumps off an out-of-range stored value. */
function SelectWithFallback({
  value,
  options,
  onChange,
  className,
}: {
  value: number;
  options: { value: number; label: string }[];
  onChange: (v: number) => void;
  className?: string;
}) {
  const known = options.some((o) => o.value === value);
  return (
    <select value={value} onChange={(e) => onChange(parseInt(e.target.value, 10))} className={className}>
      {!known && <option value={value}>Unknown ({value})</option>}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

// ─── 1. Defense system ─────────────────────────────────────────────────────

function DefenseSystemSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const showValues = stage.defenseSystem !== 0; // TrialStage's own [ShowIf]
  const showPotency = usesAttackPotency(stage.defenseSystem);
  const description = monolithDescription(stage.defenseSystem, stage.monolithValues);

  function patchValues(patch: Partial<MonolithValues>) {
    onUpdate((s) => updateMonolithValues(s, patch));
  }

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Defense details</h3>
        <label className="block space-y-1">
          <span className="text-xs text-muted-foreground">Monolith_Attack</span>
          <SelectWithFallback
            value={stage.defenseSystem}
            options={MONOLITH_EFFECTS.map((e) => ({ value: e.value, label: `${e.name} (${e.value})` }))}
            onChange={(v) => onUpdate((s) => updateDefenseSystem(s, v))}
            className={cn(selectClass, "w-full")}
          />
        </label>

        {showValues && (
          <div className="grid grid-cols-2 gap-3">
            <label className="flex items-center gap-2 text-xs col-span-2">
              <input
                type="checkbox"
                checked={stage.monolithValues.hitInvincibleState}
                onChange={(e) => patchValues({ hitInvincibleState: e.target.checked })}
              />
              hitInvincibleState{" "}
              <span className="text-muted-foreground">(no description term wired in-game)</span>
            </label>
            <NumberField
              label="activationTime"
              value={stage.monolithValues.activationTime}
              onChange={(v) => patchValues({ activationTime: v })}
            />
            {showPotency && (
              <NumberField
                label="attackPotency"
                value={stage.monolithValues.attackPotency}
                onChange={(v) => patchValues({ attackPotency: v })}
              />
            )}
            <NumberField
              label="effectDuration"
              value={stage.monolithValues.effectDuration}
              onChange={(v) => patchValues({ effectDuration: v })}
            />
          </div>
        )}

        <div className="rounded-md border border-border bg-muted/20 px-3 py-2 text-xs leading-relaxed">
          <MarkupText text={description} />
        </div>
    </section>
  );
}

// ─── 2. Modifiers ───────────────────────────────────────────────────────────

function ModifiersSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const m = stage.modifier;
  function patch(p: Partial<StageModifier>) {
    onUpdate((s) => updateModifier(s, p));
  }
  return (
    <section className="space-y-3">
      <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Multipliers</h3>
      <div className="grid grid-cols-2 gap-3">
        <NumberField label="expMultiplier" value={m.expMultiplier} onChange={(v) => patch({ expMultiplier: v })} />
        <NumberField label="goldMultiplier" value={m.goldMultiplier} onChange={(v) => patch({ goldMultiplier: v })} />
        <div>
          <NumberField
            label="damageMultiplier"
            value={m.damageMultiplier}
            onChange={(v) => patch({ damageMultiplier: v })}
          />
          {m.damageMultiplier === 0 && (
            <p className="text-[10px] text-amber-400 mt-1">0 is legal, but almost certainly unintended.</p>
          )}
        </div>
        <div>
          <NumberField label="hpMultiplier" value={m.hpMultiplier} onChange={(v) => patch({ hpMultiplier: v })} />
          {m.hpMultiplier === 0 && (
            <p className="text-[10px] text-amber-400 mt-1">0 is legal, but almost certainly unintended.</p>
          )}
        </div>
      </div>
    </section>
  );
}

// ─── 3. Waves ───────────────────────────────────────────────────────────────

const ENEMY_OPTIONS_BY_CLASS = {
  Creep: TRIAL_ENEMIES.filter((e) => e.class === "Creep"),
  Boss: TRIAL_ENEMIES.filter((e) => e.class === "Boss"),
};

function SpawnRow({
  spawn,
  onChange,
  onRemove,
}: {
  spawn: SpawnData;
  onChange: (patch: Partial<SpawnData>) => void;
  onRemove?: () => void;
}) {
  return (
    <tr className="border-t border-border/50">
      <td className="py-1 pr-2">
        <select
          value={spawn._enemyIndex}
          onChange={(e) => onChange({ _enemyIndex: parseInt(e.target.value, 10) })}
          className={cn(selectClass, "w-full")}
        >
          {!TRIAL_ENEMIES.some((e) => e.index === spawn._enemyIndex) && (
            <option value={spawn._enemyIndex}>Unknown ({spawn._enemyIndex})</option>
          )}
          <optgroup label="Creep">
            {ENEMY_OPTIONS_BY_CLASS.Creep.map((en) => (
              <option key={en.index} value={en.index}>
                {en.name}
                {en.prefab ? ` — ${en.prefab}` : " — empty slot"}
              </option>
            ))}
          </optgroup>
          <optgroup label="Boss">
            {ENEMY_OPTIONS_BY_CLASS.Boss.map((en) => (
              <option key={en.index} value={en.index}>
                {en.name}
                {en.prefab ? ` — ${en.prefab}` : " — empty slot"}
              </option>
            ))}
          </optgroup>
        </select>
      </td>
      <td className="py-1 pr-2">
        <select
          value={spawn._spawnPos}
          onChange={(e) => onChange({ _spawnPos: parseInt(e.target.value, 10) })}
          className={cn(selectClass, "w-full")}
        >
          <option value={-1}>Random position</option>
          {!TRIAL_SPAWN_POSITIONS.some((p) => p.index === spawn._spawnPos) && spawn._spawnPos !== -1 && (
            <option value={spawn._spawnPos}>Unknown ({spawn._spawnPos})</option>
          )}
          {TRIAL_SPAWN_POSITIONS.map((p) => (
            <option key={p.index} value={p.index}>
              {p.name ?? `Position ${p.index}`}
            </option>
          ))}
        </select>
      </td>
      <td className="py-1 pr-2 text-center">
        <input
          type="checkbox"
          checked={spawn._forceLevel}
          onChange={(e) => onChange({ _forceLevel: e.target.checked })}
        />
      </td>
      <td className="py-1 pr-2">
        <input
          type="number"
          value={spawn._level}
          onChange={(e) => onChange({ _level: e.target.value === "" ? 0 : Number(e.target.value) })}
          className={cn(numberClass, "w-20")}
        />
      </td>
      <td className="py-1">
        {onRemove && (
          <button type="button" onClick={onRemove} className="text-destructive text-xs px-1" aria-label="Remove spawn">
            ✕
          </button>
        )}
      </td>
    </tr>
  );
}

function WavesSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Wave setup</h3>
        <button
          type="button"
          onClick={() => onUpdate(addWave)}
          className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent"
        >
          + Add wave
        </button>
      </div>
      <div className="space-y-4">
        {stage.waveData.map((wave, wi) => (
          <div key={wi} className="rounded-md border border-border p-3 space-y-2">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-medium">Wave {wi + 1}</h4>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => onUpdate((s) => addSpawn(s, wi))}
                  className="text-[11px] text-primary hover:underline"
                >
                  + spawn
                </button>
                {stage.waveData.length > 1 && (
                  <button
                    type="button"
                    onClick={() => onUpdate((s) => removeWave(s, wi))}
                    className="text-[11px] text-destructive hover:underline"
                  >
                    remove wave
                  </button>
                )}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[420px]">
                <thead>
                  <tr className="text-muted-foreground text-left">
                    <th className="pb-1 pr-2 font-medium">Enemy</th>
                    <th className="pb-1 pr-2 font-medium">Position</th>
                    <th className="pb-1 pr-2 font-medium">Force lvl</th>
                    <th className="pb-1 pr-2 font-medium">Level</th>
                    <th className="pb-1" />
                  </tr>
                </thead>
                <tbody>
                  {wave.spawnData.map((sp, si) => (
                    <SpawnRow
                      key={si}
                      spawn={sp}
                      onChange={(patch) => onUpdate((s) => updateSpawn(s, wi, si, patch))}
                      onRemove={wave.spawnData.length > 1 ? () => onUpdate((s) => removeSpawn(s, wi, si)) : undefined}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── 4. Challenges ──────────────────────────────────────────────────────────

function ChallengesSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Challenge goals</h3>
        {stage.challenges.length < 3 && (
          <button
            type="button"
            onClick={() => onUpdate(addChallenge)}
            className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent"
          >
            + Add challenge
          </button>
        )}
      </div>
      <div className="space-y-2">
        {stage.challenges.map((c, ci) => {
          const showAmount = c.challengeType === 2 || c.challengeType === 3; // LimitedConsumable / ClearWithin
          return (
            <div key={ci} className="flex items-center gap-2">
              <SelectWithFallback
                value={c.challengeType}
                options={CHALLENGE_TYPES.map((ct) => ({ value: ct.value, label: ct.name }))}
                onChange={(v) => onUpdate((s) => updateChallenge(s, ci, { challengeType: v }))}
                className={cn(selectClass, "w-44 shrink-0")}
              />
              {showAmount && (
                <input
                  type="number"
                  value={c.amount}
                  onChange={(e) =>
                    onUpdate((s) => updateChallenge(s, ci, { amount: e.target.value === "" ? 0 : Number(e.target.value) }))
                  }
                  className={cn(numberClass, "w-20 shrink-0")}
                />
              )}
              <span className="flex-1 min-w-0 text-xs text-muted-foreground truncate">
                <MarkupText text={challengeDescription(c.challengeType, c.amount)} />
              </span>
              <button
                type="button"
                onClick={() => onUpdate((s) => removeChallenge(s, ci))}
                className="text-destructive text-xs px-1 shrink-0"
                aria-label="Remove challenge"
              >
                ✕
              </button>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ─── 5. Rewards ─────────────────────────────────────────────────────────────

function RewardsSection({ stage, onUpdate }: { stage: TrialStage; onUpdate: Updater }) {
  const shortfall = stage.reward.length < stage.challenges.length;
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Star rewards</h3>
        <button
          type="button"
          onClick={() => onUpdate(addReward)}
          className="text-xs px-2 py-1 rounded-md border border-border hover:bg-accent"
        >
          + Add reward
        </button>
      </div>
      <div className="space-y-2">
        <p className="text-[11px] text-muted-foreground">
          Rewards are indexed by star count, not granted as a list — the 1st row is the 1-star reward, and so on.
        </p>
        {shortfall && (
          <p className="text-xs text-destructive">
            {stage.reward.length} reward{stage.reward.length === 1 ? "" : "s"} but {stage.challenges.length}{" "}
            challenges — a player who clears every challenge indexes past the end of reward[] and crashes (Defect
            C).
          </p>
        )}
        {stage.reward.map((r, ri) => (
          <div key={ri} className="flex items-center gap-2 rounded-md border border-border/60 p-2">
            <span className="text-xs w-20 shrink-0 text-muted-foreground">{ordinal(ri + 1)} star</span>
            <SelectWithFallback
              value={r.rewardType}
              options={Object.entries(REWARD_TYPE_NAMES).map(([v, label]) => ({ value: Number(v), label }))}
              onChange={(v) => onUpdate((s) => updateReward(s, ri, { rewardType: v }))}
              className={cn(selectClass, "w-40 shrink-0")}
            />
            {r.rewardType === 0 ? (
              <ItemPicker
                className="flex-1 min-w-0"
                value={r.itemID}
                onChange={(id) => onUpdate((s) => updateReward(s, ri, { itemID: id }))}
              />
            ) : (
              <div className="flex-1" />
            )}
            <input
              type="number"
              value={r.amount}
              onChange={(e) =>
                onUpdate((s) => updateReward(s, ri, { amount: e.target.value === "" ? 0 : Number(e.target.value) }))
              }
              className={cn(numberClass, "w-24 shrink-0")}
              placeholder="amount"
            />
            <button
              type="button"
              onClick={() => onUpdate((s) => removeReward(s, ri))}
              className="text-destructive text-xs px-1 shrink-0"
              aria-label="Remove reward"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </section>
  );
}
